// piano-frontend/src/components/ScheduleSlotView.js
// 관리자 스케줄 현황 — 고정제(FIXED) 회원의 '주간 고정 스케줄'을 관리자 레슨 페이지 표 형식으로 한눈에 보여준다.
// 목적: (1) 전체 고정 스케줄 조망 (2) 신규 회원을 어느 요일/시간/선생님에 넣을 수 있는지 '빈자리' 찾기.
// 각 셀을 근무 선생님별 하위 칸으로 분할 — 회원이 있으면 이름(차분한 색), 없으면 초록으로 '빈자리' 강조.
// 비활성 회원은 완전히 제외(빈자리로 취급). 날짜 기반이 아닌 요일 기반의 반복 스케줄 뷰이므로 변동/휴일은 다루지 않는다.
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Paper, IconButton, FormControl, InputLabel, Select, MenuItem,
  Stack, CircularProgress, useTheme
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import api from "../api/axios";

const DAYS = [
  { value: "MON", label: "월" },
  { value: "TUE", label: "화" },
  { value: "WED", label: "수" },
  { value: "THU", label: "목" },
  { value: "FRI", label: "금" },
  { value: "SAT", label: "토" },
];

const toMin = (t) => { const [h, m] = String(t).slice(0, 5).split(":").map(Number); return h * 60 + m; };
const toTime = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

function ScheduleSlotView({ teachers = [], onMemberClick }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [slots, setSlots] = useState({});
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterTeacher, setFilterTeacher] = useState("");

  const fetchSlots = async () => {
    setLoading(true);
    try {
      const res = await api.get("/members/schedules/slots");
      setSlots(res.data.slots || {});
      setSummary(res.data.summary || null);
    } catch (err) {
      console.error("스케줄 슬롯 조회 실패:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSlots(); }, []);

  const activeTeachers = useMemo(() => teachers.filter((t) => t.is_active), [teachers]);

  // 필터 반영: 특정 선생님만 볼 때는 그 선생님만
  const shownTeachers = useMemo(
    () => (filterTeacher ? activeTeachers.filter((t) => t.id === Number(filterTeacher)) : activeTeachers),
    [activeTeachers, filterTeacher]
  );

  // 표시할 시간 슬롯: 대상 선생님들의 근무시간 범위에서 30분 단위로 생성
  const timeSlots = useMemo(() => {
    let min = Infinity, max = -Infinity;
    shownTeachers.forEach((t) => (t.schedules || []).forEach((s) => {
      min = Math.min(min, toMin(s.start));
      max = Math.max(max, toMin(s.end));
    }));
    if (!isFinite(min) || !isFinite(max)) return [];
    const out = [];
    for (let m = min; m < max; m += 30) out.push(toTime(m));
    return out;
  }, [shownTeachers]);

  // day -> "HH:MM" -> teacherId -> 점유 회원(활성 회원만).
  // 레슨 지속시간 전체(30분 초과 레슨 포함)를 30분 단위로 채워 중간 행이 빈자리로 오인되지 않게 한다.
  const occupancy = useMemo(() => {
    const map = {};
    DAYS.forEach((d) => { map[d.value] = {}; });
    Object.keys(slots).forEach((day) => {
      if (!map[day]) map[day] = {};
      Object.keys(slots[day] || {}).forEach((time) => {
        (slots[day][time] || []).forEach((m) => {
          if (!m.isActive) return; // 비활성 회원 완전 제외 → 그 자리는 빈자리로 취급
          const startM = toMin(m.startTime || time);
          let endM = toMin(m.endTime || time);
          if (!(endM > startM)) endM = startM + 30; // 종료시간 누락/이상 시 최소 한 칸
          for (let mm = startM; mm < endM; mm += 30) {
            const t = toTime(mm);
            if (!map[day][t]) map[day][t] = {};
            map[day][t][m.teacherId] = m;
          }
        });
      });
    });
    return map;
  }, [slots]);

  // 해당 요일·시간에 근무하는(표시 대상) 선생님
  const workingTeachers = (day, time) =>
    shownTeachers.filter((t) =>
      (t.schedules || []).some((s) => s.day === day && time >= s.start.slice(0, 5) && time < s.end.slice(0, 5))
    );

  // 빈자리/배정 통계
  const stats = useMemo(() => {
    let free = 0, filled = 0;
    timeSlots.forEach((time) => DAYS.forEach((d) => {
      workingTeachers(d.value, time).forEach((t) => {
        if (occupancy[d.value]?.[time]?.[t.id]) filled += 1; else free += 1;
      });
    }));
    return { free, filled };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeSlots, occupancy, shownTeachers]);

  const gridCols = "clamp(46px, 11vw, 68px) repeat(6, minmax(0, 1fr))";
  const cellBorder = isDark ? "1px solid #3a3a3a" : "1px solid #e6e6e6";
  const dayBorder = isDark ? "2px solid #555" : "2px solid #d0d0d0";

  // 색상 팔레트: 빈자리는 강조(선명한 초록), 배정된 자리는 차분하게
  const freeBg = isDark ? "#204d24" : "#b9e6bd";
  const freeText = isDark ? "#c8e6c9" : "#1b5e20";
  const filledBg = isDark ? "#2b3a47" : "#eef2f6";
  const filledText = isDark ? "#e3f2fd" : "#26343f";
  const offBg = isDark ? "#242424" : "#f4f4f4";
  const offText = isDark ? "#777" : "#b0b0b0";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
      {/* 헤더: 제목 · 필터 · 통계 */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ mb: 1.5, flexShrink: 0 }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ flexShrink: 0 }}>
          고정 스케줄 현황
        </Typography>

        {/* 모바일: 필터와 새로고침을 같은 줄에 */}
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150, flex: { xs: 1, sm: "0 0 auto" } }}>
            <InputLabel>선생님</InputLabel>
            <Select value={filterTeacher} label="선생님" onChange={(e) => setFilterTeacher(e.target.value)}>
              <MenuItem value="">전체 선생님</MenuItem>
              {activeTeachers.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <IconButton onClick={fetchSlots} disabled={loading} size="small" aria-label="새로고침">
            <Refresh />
          </IconButton>
        </Stack>

        <Box sx={{ flex: 1 }} />

        {/* 범례 + 통계 */}
        <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
          <LegendDot bg={freeBg} border={isDark ? "#3a7d40" : "#7cc684"} label={`빈자리 ${stats.free}`} bold />
          <LegendDot bg={filledBg} border={isDark ? "#3a4a57" : "#d3dde6"} label={`배정 ${stats.filled}`} />
          <LegendDot bg={offBg} border={isDark ? "#333" : "#e0e0e0"} label="미출근" muted />
          {summary && (
            <Typography variant="caption" color="text.secondary">· 고정 {summary.totalSchedules}건</Typography>
          )}
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
      ) : timeSlots.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
          표시할 선생님 근무 스케줄이 없습니다.
        </Typography>
      ) : (
        <Paper variant="outlined" sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
          {/* 요일 헤더 */}
          <Box sx={{
            display: "grid",
            gridTemplateColumns: gridCols,
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: isDark ? "grey.900" : "#f7f7f7",
            textAlign: "center",
          }}>
            <Box sx={{ p: "clamp(0.3rem, 1vw, 0.6rem)", borderRight: dayBorder, borderBottom: dayBorder }} />
            {DAYS.map((d) => (
              <Box key={d.value} sx={{
                p: "clamp(0.3rem, 1vw, 0.6rem)",
                borderRight: dayBorder,
                borderBottom: dayBorder,
                fontWeight: 700,
                fontSize: "clamp(0.68rem, 2vw, 0.9rem)",
              }}>
                {d.label}
              </Box>
            ))}
          </Box>

          {/* 시간 행 */}
          {timeSlots.map((time) => {
            const isHourStart = time.endsWith(":00");
            return (
              <Box key={time} sx={{
                display: "grid",
                gridTemplateColumns: gridCols,
                borderTop: isHourStart ? dayBorder : cellBorder,
              }}>
                <Box sx={{
                  p: "clamp(0.2rem, 0.7vw, 0.5rem)",
                  bgcolor: isDark ? "grey.900" : "#f7f7f7",
                  borderRight: dayBorder,
                  fontSize: "clamp(0.55rem, 1.7vw, 0.78rem)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: isHourStart ? 700 : 500,
                  color: isHourStart ? "text.primary" : "text.secondary",
                }}>
                  {time}
                </Box>

                {DAYS.map((d) => {
                  const working = workingTeachers(d.value, time);
                  return (
                    <Box key={d.value} sx={{
                      p: "clamp(0.15rem, 0.6vw, 0.4rem)",
                      borderRight: dayBorder,
                      display: "flex",
                      flexDirection: "column",
                      gap: "clamp(0.1rem, 0.4vw, 0.25rem)",
                      minHeight: "clamp(26px, 8vw, 34px)",
                    }}>
                      {working.length === 0 ? (
                        <Box sx={{
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: offBg,
                          color: offText,
                          fontSize: "clamp(0.5rem, 1.5vw, 0.66rem)",
                          borderRadius: 0.75,
                          minHeight: "clamp(22px, 7vw, 28px)",
                        }}>
                          미출근
                        </Box>
                      ) : (
                        working.map((t) => {
                          const occ = occupancy[d.value]?.[time]?.[t.id];
                          const filled = !!occ;
                          return (
                            <Box
                              key={t.id}
                              onClick={filled && onMemberClick ? () => onMemberClick(occ.memberId) : undefined}
                              title={filled
                                ? `${occ.memberName} · ${t.name} 선생님 (${time})`
                                : `${t.name} 선생님 · 빈자리 (${time})`}
                              sx={{
                                borderRadius: 0.75,
                                px: "clamp(0.2rem, 0.8vw, 0.45rem)",
                                py: "clamp(0.12rem, 0.5vw, 0.3rem)",
                                minHeight: "clamp(22px, 7vw, 28px)",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                lineHeight: 1.15,
                                textAlign: "center",
                                cursor: filled ? "pointer" : "default",
                                bgcolor: filled ? filledBg : freeBg,
                                color: filled ? filledText : freeText,
                                border: filled
                                  ? (isDark ? "1px solid #3a4a57" : "1px solid #d9e2ea")
                                  : (isDark ? "1px solid #3a7d40" : "1px solid #86cf8e"),
                                transition: "filter .12s",
                                "&:hover": filled ? { filter: isDark ? "brightness(1.25)" : "brightness(0.97)" } : {},
                              }}
                            >
                              {filled ? (
                                <>
                                  <Box component="span" sx={{
                                    fontWeight: 700,
                                    fontSize: "clamp(0.56rem, 1.8vw, 0.76rem)",
                                    maxWidth: "100%",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}>
                                    {occ.memberName}
                                  </Box>
                                  <Box component="span" sx={{ fontSize: "clamp(0.44rem, 1.4vw, 0.6rem)", opacity: 0.75, whiteSpace: "nowrap" }}>
                                    {t.name}
                                  </Box>
                                </>
                              ) : (
                                <>
                                  <Box component="span" sx={{ fontWeight: 700, fontSize: "clamp(0.5rem, 1.6vw, 0.68rem)", whiteSpace: "nowrap" }}>
                                    빈자리
                                  </Box>
                                  <Box component="span" sx={{ fontSize: "clamp(0.44rem, 1.4vw, 0.6rem)", opacity: 0.85, whiteSpace: "nowrap" }}>
                                    {t.name}
                                  </Box>
                                </>
                              )}
                            </Box>
                          );
                        })
                      )}
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Paper>
      )}
    </Box>
  );
}

function LegendDot({ bg, border, label, bold, muted }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: bg, border: `1px solid ${border}`, flexShrink: 0 }} />
      <Typography variant="caption" color={muted ? "text.disabled" : "text.secondary"} fontWeight={bold ? 700 : 400}>
        {label}
      </Typography>
    </Stack>
  );
}

export default ScheduleSlotView;
